/*
 * This file, when included, patches both XMLHttpRequest and fetch() to
 * include the Rails CSRF token in all same-domain requests.
 * Based on https://github.com/rtfeldman/csrf-xhr/blob/master/csrf-xhr.js
 *
 * Why are we monkey patching XMLHttpRequest and Fetch? Read the solution preview for this change for more context.
 * https://cultureamp.atlassian.net/wiki/spaces/TV/blog/2019/12/05/982417970/Automatic+injection+of+CSRF+tokens+in+murmur+JS
 */

// h/t http://stackoverflow.com/a/19709846/2334666
const absoluteUrl = /^(?:[a-z]+:)?\/\//i;

// h/t http://tosbourn.com/a-fix-for-window-location-origin-in-internet-explorer/
//
// Use this instead of location.origin, which is not supported in
// older browsers. Always do it this way, even when location.origin is
// available, to ensure consistency across browsers.
const compiledOrigin =
  location.protocol +
  '//' +
  location.hostname +
  (location.port ? ':' + location.port : '');

function isLocalOrigin(url: string) {
  // If we're on http://example.com and the URL starts with
  // http://example.com then we're sending to a local origin.
  // Also if we're sending to a relative URL, that's also a local origin.
  return url.indexOf(compiledOrigin) === 0 || !absoluteUrl.test(url);
}

let csrfTokenNode: null | HTMLMetaElement = null;
let csrfToken: null | string = null;

try {
  csrfTokenNode = document.head.querySelector('meta[name="csrf-token"]');
  if (csrfTokenNode !== null) {
    csrfToken = csrfTokenNode.content;
  }
} catch (e) {
  // ignore document-based errors
}

class CAXMLHttpRequest extends XMLHttpRequest {
  open(method: string, url: string | URL) {
    super.open(method, url);
    if (isLocalOrigin(url.toString()) && csrfToken !== null) {
      this.setRequestHeader('X-CSRF-Token', csrfToken);
    }
  }
}

if (csrfToken === null) {
  // eslint-disable-next-line no-console
  console.warn(
    'csrf-patch was unable to add X-CSRF-Token headers to XMLHttpRequest.open() and fetch() becasue it could not find a <meta> element in the <head> with name="csrf-token" and a Rails CSRF token in a content attribute.'
  );
} else {
  const tempCrsfToken = csrfToken;
  window.XMLHttpRequest = CAXMLHttpRequest;

  // Patch fetch
  const originalFetch = window.fetch.bind(window);
  window.fetch = (resource, init) => {
    if (isLocalOrigin(resource.toString())) {
      let originalHeaders = {};
      if (init && init.headers) originalHeaders = init.headers;
      const headers = new Headers(originalHeaders);
      headers.set('X-CSRF-Token', tempCrsfToken);
      return originalFetch(resource, { ...init, headers });
    }
    return originalFetch(resource, init);
  };
}
